// src/components/admin/ProductDialog.jsx - 使用后端返回的绝对路径
import React, { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';
import { categoryApi, unitApi, uploadApi } from '../../api';

const ProductDialog = ({ product, onClose, onSubmit }) => {
  const { language } = useApp();
  const t = translations[language];
  
  const [formData, setFormData] = useState({
    barcode: '',
    name: '',
    name_en: '',
    name_km: '',
    category_id: '',
    unit_id: '',
    cost_price: '',
    sale_price: '',
    stock: '',
    min_stock: '',
    image: ''
  });

  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadCategories();
    loadUnits();
    
    if (product) {
      console.log('📝 编辑模式 - 加载商品数据:', product);
      
      setFormData({
        barcode: product.barcode || '',
        name: product.name || '',
        name_en: product.name_en || '',
        name_km: product.name_km || '',
        category_id: product.category_id || '',
        unit_id: product.unit_id || '',
        cost_price: product.cost_price || '',
        sale_price: product.sale_price || '',
        stock: product.stock || '',
        min_stock: product.min_stock || '',
        image: product.image || ''
      });
      
      if (product.image) {
        console.log('🖼️ 设置图片预览:', product.image);
        setImagePreview(product.image);  // ← 直接用完整URL
      }
    }
  }, [product]);

  const loadCategories = async () => {
    try {
      const res = await categoryApi.getList();
      if (res.success) {
        setCategories(res.data || []);
      }
    } catch (err) {
      console.error('加载分类失败:', err);
    }
  };

  const loadUnits = async () => {
    try {
      const res = await unitApi.getList({ is_sellable: 1, status: 'active' });
      if (res.success) {
        setUnits(res.data || []);
      }
    } catch (err) {
      console.error('加载单位失败:', err);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 文件大小限制 5MB
    if (file.size > 5 * 1024 * 1024) {
      alert(
        language === 'en' ? 'Image size must be less than 5MB' :
        language === 'km' ? 'ទំហំរូបភាពត្រូវតែតិចជាង 5MB' :
        '图片大小不能超过 5MB'
      );
      return;
    }

    // 文件类型检查
    if (!file.type.startsWith('image/')) {
      alert(
        language === 'en' ? 'Please select an image file' :
        language === 'km' ? 'សូមជ្រើសរើសឯកសាររូបភាព' :
        '请选择图片文件'
      );
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🚀 开始提交商品表单');
    
    let imagePath = formData.image;
    console.log('📍 初始 image:', imagePath);
    
    // 如果有新图片,先上传
    if (imageFile) {
      setUploading(true);
      console.log('📤 开始上传图片...');
      
      try {
        const uploadRes = await uploadApi.uploadProductImage(imageFile);
        
        console.log('✅ 上传响应:', uploadRes);
        
        if (uploadRes.success && uploadRes.data && uploadRes.data.url) {
          // ⭐ 后端返回完整URL,直接使用!
          imagePath = uploadRes.data.url;
          console.log('✅ 获取到完整图片URL:', imagePath);
        } else {
          console.error('❌ 上传失败:', uploadRes);
          alert(
            language === 'en' ? 'Image upload failed' :
            language === 'km' ? 'បរាជ័យក្នុងការផ្ទុករូបភាពឡើង' :
            '图片上传失败: ' + (uploadRes.message || '未知错误')
          );
          setUploading(false);
          return;
        }
      } catch (err) {
        console.error('❌ 上传异常:', err);
        alert(
          language === 'en' ? 'Image upload failed' :
          language === 'km' ? 'បរាជ័យក្នុងការផ្ទុករូបភាពឡើង' :
          '图片上传失败: ' + err.message
        );
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    const submitData = {
      ...formData,
      image: imagePath || '',
      cost_price: parseFloat(formData.cost_price) || 0,
      sale_price: parseFloat(formData.sale_price) || 0,
      stock: parseInt(formData.stock) || 0,
      min_stock: parseInt(formData.min_stock) || 0
    };

    console.log('📤 最终提交数据:', submitData);
    onSubmit(submitData);
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const getCategoryName = (category) => {
    switch (language) {
      case 'en':
        return category.name_en || category.name;
      case 'km':
        return category.name_km || category.name;
      default:
        return category.name;
    }
  };

  const getUnitName = (unit) => {
    switch (language) {
      case 'en':
        return unit.name_en || unit.name;
      case 'km':
        return unit.name_km || unit.name;
      default:
        return unit.name;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <h3 className="text-xl font-bold">
            {product ? (
              language === 'en' ? 'Edit Product' :
              language === 'km' ? 'កែសម្រួលទំនិញ' :
              '编辑商品'
            ) : (
              language === 'en' ? 'Add Product' :
              language === 'km' ? 'បន្ថែមទំនិញ' :
              '添加商品'
            )}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* 商品图片 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'en' ? 'Product Image' :
               language === 'km' ? 'រូបភាពទំនិញ' :
               '商品图片'}
            </label>
            
            <div className="flex items-start space-x-4">
              {/* 图片预览 */}
              <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon className="w-12 h-12 text-gray-400" />
                )}
              </div>

              {/* 上传按钮 */}
              <div className="flex-1">
                <input
                  type="file"
                  id="imageUpload"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <label
                  htmlFor="imageUpload"
                  className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer transition-colors"
                >
                  <Upload className="w-5 h-5 mr-2" />
                  {language === 'en' ? 'Upload Image' :
                   language === 'km' ? 'ផ្ទុករូបភាពឡើង' :
                   '上传图片'}
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  {language === 'en' ? 'Support JPG, PNG, max 5MB' :
                   language === 'km' ? 'គាំទ្រ JPG, PNG, អតិបរមា 5MB' :
                   '支持 JPG、PNG,最大 5MB'}
                </p>
              </div>
            </div>
          </div>

          {/* 基本信息 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 条码 */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'en' ? 'Barcode' :
                 language === 'km' ? 'បាកូដ' :
                 '条码'}
              </label>
              <input
                type="text"
                value={formData.barcode}
                onChange={(e) => handleChange('barcode', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={language === 'en' ? 'Enter barcode' : language === 'km' ? 'បញ្ចូលបាកូដ' : '输入条码'}
              />
            </div>

            {/* 分类 */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'en' ? 'Category *' :
                 language === 'km' ? 'ប្រភេទ *' :
                 '分类 *'}
              </label>
              <select
                value={formData.category_id}
                onChange={(e) => handleChange('category_id', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">
                  {language === 'en' ? 'Select category' :
                   language === 'km' ? 'ជ្រើសរើសប្រភេទ' :
                   '选择分类'}
                </option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {getCategoryName(cat)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 商品名称(三语) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'zh' ? '商品名称 (中文) *' :
                 language === 'en' ? 'Product Name (Chinese) *' :
                 'ឈ្មោះទំនិញ (ចិន) *'}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'zh' ? '商品名称 (英文)' :
                 language === 'en' ? 'Product Name (English)' :
                 'ឈ្មោះទំនិញ (អង់គ្លេស)'}
              </label>
              <input
                type="text"
                value={formData.name_en}
                onChange={(e) => handleChange('name_en', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'zh' ? '商品名称 (高棉语)' :
                 language === 'en' ? 'Product Name (Khmer)' :
                 'ឈ្មោះទំនិញ (ខ្មែរ)'}
              </label>
              <input
                type="text"
                value={formData.name_km}
                onChange={(e) => handleChange('name_km', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* 单位和价格 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 单位 */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'en' ? 'Unit *' :
                 language === 'km' ? 'ឯកតា *' :
                 '单位 *'}
              </label>
              <select
                value={formData.unit_id}
                onChange={(e) => handleChange('unit_id', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">
                  {language === 'en' ? 'Select unit' :
                   language === 'km' ? 'ជ្រើសរើសឯកតា' :
                   '选择单位'}
                </option>
                {units.map(unit => (
                  <option key={unit.id} value={unit.id}>
                    {getUnitName(unit)}
                  </option>
                ))}
              </select>
            </div>

            {/* 成本价 */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'en' ? 'Cost Price *' :
                 language === 'km' ? 'តម្លៃដើម *' :
                 '成本价 *'}
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.cost_price}
                onChange={(e) => handleChange('cost_price', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* 售价 */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'en' ? 'Sale Price *' :
                 language === 'km' ? 'តម្លៃលក់ *' :
                 '售价 *'}
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.sale_price}
                onChange={(e) => handleChange('sale_price', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* 库存 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'en' ? 'Stock *' :
                 language === 'km' ? 'ស្តុក *' :
                 '库存 *'}
              </label>
              <input
                type="number"
                value={formData.stock}
                onChange={(e) => handleChange('stock', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'en' ? 'Minimum Stock' :
                 language === 'km' ? 'ស្តុកអប្បបរមា' :
                 '最低库存'}
              </label>
              <input
                type="number"
                value={formData.min_stock}
                onChange={(e) => handleChange('min_stock', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* 按钮 */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                language === 'en' ? 'Uploading...' :
                language === 'km' ? 'កំពុងផ្ទុកឡើង...' :
                '上传中...'
              ) : product ? t.save : (
                language === 'en' ? 'Add' :
                language === 'km' ? 'បន្ថែម' :
                '添加'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductDialog;